import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionService } from '@/services/subscriptionService';

export function useSubscriptionData() {
  const { i18n } = useTranslation();
  const [subscribed, setSubscribed] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [cancelAt, setCancelAt] = useState<string | null>(null);
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const checkInProgress = useRef(false);

  const checkSubscription = useCallback(async (userId?: string) => {
    if (!userId) {
      console.log('[SUBSCRIPTION DATA] No user ID, skipping');
      setLoading(false);
      return;
    }

    // Prevent concurrent checks
    if (checkInProgress.current) {
      console.log('[SUBSCRIPTION DATA] Check already in progress, skipping');
      return;
    }

    checkInProgress.current = true;
    setLoading(true);
    
    try {
      const result = await SubscriptionService.checkSubscription(userId);
      
      setSubscribed(result.subscribed);
      setProductId(result.product_id || null);
      setSubscriptionEnd(result.subscription_end || null);
      setTrialEnd(result.trial_end || null);
      setCancelAtPeriodEnd(result.cancel_at_period_end || false);
      setCancelAt(result.cancel_at || null);
      setStripeCustomerId(result.stripe_customer_id || null);
      
      console.log('[SUBSCRIPTION DATA] State updated. subscribed=', result.subscribed, 'trial_end=', result.trial_end, 'stripe_customer_id=', result.stripe_customer_id);
    } catch (error) {
      console.error('[SUBSCRIPTION DATA] Unexpected error:', error);
      setSubscribed(false);
      setProductId(null);
      setSubscriptionEnd(null);
      setTrialEnd(null);
      setCancelAtPeriodEnd(false);
      setCancelAt(null);
      setStripeCustomerId(null);
    } finally {
      setLoading(false);
      checkInProgress.current = false;
    }
  }, []);

  const createCheckout = useCallback(async () => {
    await SubscriptionService.createCheckout(i18n.language);
  }, [i18n.language]);

  const openCustomerPortal = useCallback(async () => {
    await SubscriptionService.openCustomerPortal(i18n.language);
  }, [i18n.language]);

  return {
    subscribed,
    productId,
    subscriptionEnd,
    trialEnd,
    cancelAtPeriodEnd,
    cancelAt,
    stripeCustomerId,
    loading,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
  };
}
